import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  claimGuestSession,
  getStoredGuestSession,
} from "../../services/guestSessionService";
import { useAuthMode } from "../../hooks/useAuthMode";
import { AuthContext, AuthContextInterface } from "../../contexts";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { getTracks } from "../../pages/Tracks/tracksHandler";
import { TrackObj } from "../../pages/MyTracks/types";
import ClaimMigrateDialog, {
  ClaimMigrateSummary,
} from "./ClaimMigrateDialog";
import ZeroUploadsToast from "./ZeroUploadsToast";
import ExpireGraceModal, { ExpireGraceArtwork } from "./ExpireGraceModal";
import Mixpanel from "../../utils/mixpanel";

/**
 * GuestClaimGate is the single mount point that ties the three guest →
 * signed-in transitions together:
 *
 *   1. Just signed in with a stored guest token → call ClaimGuestSession,
 *      render the migration confirmation (or zero-state toast).
 *   2. Still a guest with <36h on the clock → fire the day-6 grace modal
 *      once per browser session.
 *
 * App.tsx mounts a single instance alongside the GuestShellBanner; render
 * cost is zero when there is nothing to do.
 */

const expireWarnWindowMs = 36 * 60 * 60 * 1000;
const expireShownKey = "ommuse.guest.expireShown";

const GuestClaimGate: React.FC = () => {
  const navigate = useNavigate();
  const { mode, guest } = useAuthMode();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();

  const claimInFlight = useRef(false);
  const [claimSummary, setClaimSummary] = useState<ClaimMigrateSummary | null>(
    null
  );
  const [zeroToast, setZeroToast] = useState(false);
  const [showExpire, setShowExpire] = useState(false);
  const [guestTracks, setGuestTracks] = useState<TrackObj[]>([]);

  // Claim-on-signup: when Auth0 reports authenticated and we still see a
  // stored guest token, exchange it. The claim service clears the token in
  // its finally block so retries don't double-fire; the in-flight ref is a
  // belt-and-braces guard against React strict-mode double-effects.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (claimInFlight.current) return;
    const stored = getStoredGuestSession();
    if (!stored) {
      // Authed user with no stored guest token. Either a normal returning
      // user OR a signup that started in a different browser (work orphaned).
      // The funnel distinguishes the two: guest_session_started count vs.
      // guest_claim_skipped_no_token count on the same distinct_id window.
      Mixpanel.track("guest_claim_skipped_no_token");
      return;
    }
    claimInFlight.current = true;
    Mixpanel.track("guest_claim_attempted", { sid: stored.sessionId });
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await claimGuestSession(token);
        if (!resp) return;
        const summary: ClaimMigrateSummary = {
          tracks: resp.getTracksClaimed(),
          projects: resp.getProjectsClaimed(),
          mbUsed: Number(resp.getMbUsed()),
        };
        Mixpanel.track("guest_claim_success", {
          ...summary,
          wasZero: resp.getWasZero(),
        });
        if (resp.getWasZero()) {
          setZeroToast(true);
        } else {
          setClaimSummary(summary);
        }
      } catch (err) {
        const code = (err as { code?: number })?.code;
        if (code === 9 /* FAILED_PRECONDITION — another claim raced us; the winner already bound this user's tracks */) {
          Mixpanel.track("guest_claim_already_done");
          return;
        }
        Mixpanel.track("guest_claim_failed", { code: code ?? "unknown" });
        toast.error("Couldn't move your guest work to your new account.");
      } finally {
        claimInFlight.current = false;
      }
    })();
  }, [isAuthenticated, getAccessTokenSilently]);

  // Day-6 grace modal: trigger once per browser session when the guest opens
  // the app and has less than expireWarnWindowMs remaining.
  useEffect(() => {
    if (mode !== "guest" || !guest) return;
    try {
      if (sessionStorage.getItem(expireShownKey)) return;
    } catch {
      /* sessionStorage unavailable; show every time, harmless */
    }
    if (guest.expiresAtMs - Date.now() < expireWarnWindowMs) {
      try {
        sessionStorage.setItem(expireShownKey, "1");
      } catch {
        /* noop */
      }
      Mixpanel.track("guest_expire_warning_shown", {
        msRemaining: guest.expiresAtMs - Date.now(),
      });
      setShowExpire(true);
    }
  }, [mode, guest]);

  // Real track count + artwork for the grace modal (was hardcoded to
  // tracks: 0 / artworks: [] — fetch the guest's own tracks the same way
  // Hub/Dashboard do, scoped to this guest's userId).
  useEffect(() => {
    if (!showExpire || !userId) return;
    (async () => {
      try {
        const token = await getValidAccessToken();
        await getTracks(
          userId,
          {
            userIds: [],
            sortBy: "Last Modified",
            isAscending: false,
            genreList: [],
            artistList: [],
            collaboratorsList: [],
            TrackPrivacy: "",
            page: 1,
            limit: 50,
            bpmStartValue: 0,
            bpmEndValue: 500,
            status: "",
            isCompletedTracks: false,
          },
          token,
          setGuestTracks,
          1
        );
      } catch {
        setGuestTracks([]);
      }
    })();
  }, [showExpire, userId, getValidAccessToken]);

  const expireSummary = useMemo(() => {
    if (mode !== "guest" || !guest) return null;
    const artworks: ExpireGraceArtwork[] = guestTracks.slice(0, 3).map((t) => ({
      src:
        t.track?.getImageUrl() && t.track.getImageUrl().includes("http")
          ? t.track.getImageUrl()
          : undefined,
      label: t.track?.getTitle(),
    }));
    return {
      tracks: guestTracks.length,
      mbUsed: Math.round(guest.usedBytes / (1024 * 1024)),
      artworks,
    };
  }, [mode, guest, guestTracks]);

  const onSignupFromExpire = useCallback(() => {
    Mixpanel.track("guest_expire_signup_clicked");
    loginWithRedirect({
      appState: { targetUrl: "/initial-setting" },
      authorizationParams: { screen_hint: "signup" },
    });
  }, [loginWithRedirect]);

  const onRemindLater = useCallback(() => {
    Mixpanel.track("guest_expire_remind_later_clicked");
    setShowExpire(false);
    try {
      // Snooze 4 hours: write a future-stamped sentinel that the next mount
      // checks before re-showing.
      sessionStorage.setItem(expireShownKey, String(Date.now() + 4 * 60 * 60 * 1000));
    } catch {
      /* noop */
    }
  }, []);

  return (
    <>
      <ClaimMigrateDialog
        open={Boolean(claimSummary)}
        summary={claimSummary ?? { tracks: 0, projects: 0, mbUsed: 0 }}
        onContinue={() => {
          setClaimSummary(null);
          navigate("/my-tracks");
        }}
      />
      <ZeroUploadsToast
        open={zeroToast}
        onClose={() => setZeroToast(false)}
      />
      {expireSummary && (
        <ExpireGraceModal
          open={showExpire}
          trackCount={expireSummary.tracks}
          mbUsed={expireSummary.mbUsed}
          artworks={expireSummary.artworks}
          onSignup={onSignupFromExpire}
          onRemindLater={onRemindLater}
        />
      )}
    </>
  );
};

export default GuestClaimGate;
